// Table
import { BasicTable } from "./BasicTable";
import { Entity } from "../Entity";
import { ExistenceStatus } from "../EntityStatus";
import { PassThroughSerializationHandler } from "./persistence/PassThroughSerializationHandler";
/**
 * EntityTable implementation
 *
 * @deprecated use `table/EntityTable` instead
 */ export class EntityTable extends BasicTable {
    add(id, value) {
        if (this.get(id)) {
            throw new Error(`IdAlreadyInUse [Table=${this.name}][Id=${id}]`);
        }
        const entity = this.createEntity(id, value);
        super.set(id, entity);
        return entity;
    }
    set(id) {
        /**
         * For IEntity objects, the regular push/commitDelta methods should be used to update the entity.
         * This is to ensure that any updates to the entity are correctly indexed and flagged for persistence.
         */ throw new Error(`SetNotAllowedOnAnEntityTable [Table=${this.name}][Id=${id}]`);
    }
    reKey(id, newId) {
        super.reKey(id, newId);
        // Update the id of the entity so that subsequent changes are correctly indexed
        const entity = this.getNonNull(newId);
        entity.setId(newId);
    }
    /**
     * Helper to create an entity object from a baseline value
     * @param id Entity id
     * @param baseline Baseline value of the entity
     */ createEntity(id, baseline) {
        return new IndexedPersistedEntity(baseline, id, this.applyIndexedUpdate.bind(this), this.flag.bind(this));
    }
    /**
     * Create a new EntityTable
     * @param tableName The name of the table
     * @param indexDefinitions Index definitions for the table
     * @param serializationHandler Serialization handler that defines how entities are are converted to and from a serializable
     */ constructor(tableName, indexDefinitions = {}, serializationHandler = new PassThroughSerializationHandler()){
        /**
         * As this table stores "IEntity<T>" objects, the index definitions need to be defined as such.
         * Although we still do not want the caller to define indexes in terms of IEntity<T>, as defining
         * indexes in terms of "T" directly is much more intuitive.
         *
         * To handle this, we convert the index definitions from "T" to "IEntity<T>". For example-
         * { index1: (v) => v.property } becomes { index1: (e) => e.value.property }
         */ const entityIndexDefintions = Object.keys(indexDefinitions).reduce((acc, key)=>{
            /**
             * IMPORTANT: Indexes are based on "entity.value" and not "entity.baseline" because
             * we want indexes to reflect uncommitted updates.
             */ acc[key] = (entity)=>indexDefinitions[key](entity.value);
            return acc;
        }, {});
        /**
         * For similar reasons, we want to morph the seriazlier from "T" (easier to define for the caller) to
         * "IEntity<T>" (needed by this class).
         */ const entitySerializationHandler = {
            /**
             * IMPORTANT: We serialize "entity.baseline" and not "entity.value" because
             * we do not want to persist uncommitted deltas until there is a proper
             * mechanism to re-sync those deltas upon re-boot (fully-offline).
             */ toSerializable: (key, value)=>serializationHandler.toSerializable(key, value.baseline),
            fromSerializable: (key, serializableValue)=>this.createEntity(key, serializationHandler.fromSerializable(key, serializableValue))
        };
        super(tableName, entityIndexDefintions, entitySerializationHandler);
    }
}
/**
 * Entity that supports indexing and persistence. This is a private class and is
 * exposed as only an IEntity<T> to the caller via the EntityTable.
 */ class IndexedPersistedEntity extends Entity {
    /**
     * Update the id of the entity
     * @param newId New id for the entity
     */ setId(newId) {
        this.id = newId;
    }
    pushDelta(changes, deltaKey) {
        this.apply(()=>super.pushDelta(changes, deltaKey));
    }
    revertDelta(deltaKey) {
        this.apply(()=>super.revertDelta(deltaKey));
    }
    commitDelta(deltaKey, additionalChanges) {
        this.apply(()=>super.commitDelta(deltaKey, additionalChanges));
    }
    updateBaseline(changes) {
        this.apply(()=>super.updateBaseline(changes));
        /**
         * Entities are only marked for cache update when their baseline updates.
         * Deltas are currently discarded. When Planner becomes a fully offline
         * app, deltas will need to be persisted as well.
         */ this.flag(this.id);
    }
    /**
     * Soft delete the entity - This removes the entry from indexes while keeping it around
     * in the table so that it can be undeleted if needed.
     */ softDelete() {
        this.apply(()=>super.softDelete());
    }
    undelete() {
        this.apply(()=>super.undelete());
    }
    /**
     * Apply an update to the entity and update indexes
     * @param update Update to apply
     */ apply(update) {
        this.applyIndexedUpdate(this.id, update, ()=>this.status === ExistenceStatus.SoftDeleted ? null : this);
    }
    /**
     * Creates an instance of IndexedPersistedEntity.
     * @param baseline Baseline value of the entity
     * @param id Entity id
     * @param applyIndexedUpdate Callback to apply an update to the entity and update indexes
     * @param flag Callback to flag the entity for persistence
     */ constructor(baseline, id, applyIndexedUpdate, flag){
        super(baseline), this.id = id, this.applyIndexedUpdate = applyIndexedUpdate, this.flag = flag;
    }
}
